// The custom shapes' geometry inside their box (board-core/BoardShapes.kt), for drawing.

import type { Box, CustomShape } from "./model.ts";

type Vec = [number, number];
const ARC_STEPS = 12;
const CLOUD_BUMPS = 7;
const CLOUD_NOTCH = 0.16;
const STAR_INNER = 0.42;

export function outline(shape: CustomShape, b: Box): Vec[] {
  const x = b.minX;
  const y = b.minY;
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  const cx = x + w / 2;
  const cy = y + h / 2;
  switch (shape) {
    case "triangle":
      return [[cx, y], [x + w, y + h], [x, y + h]];
    case "parallelogram": {
      const s = Math.min(w * 0.2, h * 0.6);
      return [[x + s, y], [x + w, y], [x + w - s, y + h], [x, y + h]];
    }
    case "trapezoid": {
      const s = Math.min(w * 0.2, h * 0.6);
      return [[x + s, y], [x + w - s, y], [x + w, y + h], [x, y + h]];
    }
    case "pentagon":
      return Array.from({ length: 5 }, (_, i) => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
        return [cx + (w / 2) * Math.cos(a), cy + (h / 2) * Math.sin(a)] as Vec;
      });
    case "hexagon": {
      const t = Math.min(w * 0.25, h * 0.5);
      return [[x + t, y], [x + w - t, y], [x + w, cy], [x + w - t, y + h], [x + t, y + h], [x, cy]];
    }
    case "star":
      return Array.from({ length: 10 }, (_, i) => {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const f = i % 2 === 0 ? 1 : STAR_INNER;
        return [cx + (w / 2) * f * Math.cos(a), cy + (h / 2) * f * Math.sin(a)] as Vec;
      });
    case "block_arrow": {
      const head = Math.min(w * 0.4, h * 0.6);
      const shaft = h * 0.25;
      return [[x, y + shaft], [x + w - head, y + shaft], [x + w - head, y], [x + w, cy], [x + w - head, y + h], [x + w - head, y + h - shaft], [x, y + h - shaft]];
    }
    case "cylinder": {
      const ry = Math.min(w * 0.18, h * 0.25);
      return [...arc(cx, w / 2, y + ry, ry, false), ...arc(cx, w / 2, y + h - ry, ry, true)];
    }
    case "cloud": {
      const n = CLOUD_BUMPS * ARC_STEPS;
      return Array.from({ length: n }, (_, i) => {
        const a = (i * 2 * Math.PI) / n;
        const f = 1 - CLOUD_NOTCH + CLOUD_NOTCH * Math.abs(Math.sin((CLOUD_BUMPS * a) / 2));
        return [cx + (w / 2) * f * Math.cos(a), cy + (h / 2) * f * Math.sin(a)] as Vec;
      });
    }
    case "document": {
      const wave = Math.min(h * 0.12, 16);
      const base = y + h - wave;
      const steps = 2 * ARC_STEPS;
      return [[x, y], [x + w, y], ...Array.from({ length: steps + 1 }, (_, i) => [x + w - (w * i) / steps, base + wave * Math.sin((2 * Math.PI * i) / steps)] as Vec)];
    }
  }
}

/** The top rim's lower half of a cylinder (drawn as the front edge of the lid). */
export function cylinderLid(b: Box): Vec[] {
  const w = b.maxX - b.minX;
  const ry = Math.min(w * 0.18, (b.maxY - b.minY) * 0.25);
  return arc(b.minX + w / 2, w / 2, b.minY + ry, ry, true);
}

function arc(cx: number, rx: number, cy: number, ry: number, lower: boolean): Vec[] {
  return Array.from({ length: ARC_STEPS + 1 }, (_, i) => {
    const a = lower ? (i * Math.PI) / ARC_STEPS : Math.PI + (i * Math.PI) / ARC_STEPS;
    return [cx + rx * Math.cos(a), cy + ry * Math.sin(a)] as Vec;
  });
}
